import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowRight, CheckCircle, Shield, Globe, ChevronRight,
  FileText, Users, Zap, Award, Search, Lock
} from 'lucide-react';
import { useData } from '../context/DataContext';
import JournalCard from '../components/JournalCard';
import AcademicBoardCard from '../components/AcademicBoardCard';
import { useAuth } from '../context/AuthContext';

const TRUST_POINTS = [
  { icon: <Users size={16} className="text-[#1A73E8]" />, title: 'Open to All Researchers', desc: 'Welcoming submissions from academic institutions and independent researchers worldwide.' },
  { icon: <CheckCircle size={16} className="text-[#1A73E8]" />, title: 'Peer-Reviewed Publication', desc: 'All submissions undergo structured double-blind review by qualified domain experts.' },
  { icon: <Shield size={16} className="text-[#1A73E8]" />, title: 'Transparent Editorial Workflow', desc: 'Authors are informed at every stage — from submission receipt to final decision.' },
  { icon: <Lock size={16} className="text-[#1A73E8]" />, title: 'Strict Submission & Review Criteria', desc: 'Manuscripts are evaluated against defined academic and methodological standards.' },
  { icon: <Globe size={16} className="text-[#1A73E8]" />, title: 'Global Research Accessibility', desc: 'Published work is indexed and made accessible to the international research community.' },
];

const HOW_IT_WORKS = [
  {
    step: '01',
    icon: <FileText size={22} className="text-[#1A73E8]" />,
    title: 'Submit Your Research',
    desc: 'Complete the structured online submission form. Upload your manuscript, provide author details, and select the most appropriate journal for your field.',
  },
  {
    step: '02',
    icon: <Users size={22} className="text-[#1A73E8]" />,
    title: 'Editorial & Peer Review',
    desc: 'Your submission is assessed by the editorial team for scope and quality, then assigned to qualified peer reviewers for double-blind evaluation.',
  },
  {
    step: '03',
    icon: <Globe size={22} className="text-[#1A73E8]" />,
    title: 'Publication & Global Visibility',
    desc: 'Accepted manuscripts are formatted, assigned a DOI, and published in the journal. Your research becomes globally discoverable via major indexing databases.',
  },
];

const FEATURES = [
  {
    icon: <CheckCircle size={20} className="text-[#1A73E8]" />,
    title: 'Rigorous Peer Review',
    description: 'Every submission undergoes double-blind peer review by domain experts. We maintain the highest standards of scholarly rigor and editorial independence.',
  },
  {
    icon: <Shield size={20} className="text-[#1A73E8]" />,
    title: 'Transparent Process',
    description: 'Authors receive structured feedback at each stage. Our transparent workflow ensures accountability and continuous improvement in research quality.',
  },
  {
    icon: <Globe size={20} className="text-[#1A73E8]" />,
    title: 'Global Reach & Discoverability',
    description: 'Publications are assigned DOIs and archived via Zenodo, ensuring your research is accessible through major scholarly platforms, search engines, and open academic repositories.',
  },
];

const AI_TOOLS = [
  {
    icon: <Search size={18} className="text-[#1A73E8]" />,
    title: 'AI Abstract Summarizer',
    desc: 'Automatically generate structured, standardized abstracts from full-text manuscripts to accelerate the submission process.',
  },
  {
    icon: <Users size={18} className="text-[#1A73E8]" />,
    title: 'AI Reviewer Matching',
    desc: 'Intelligently match submitted papers with qualified peer reviewers based on expertise, publication history, and conflict-of-interest screening.',
  },
  {
    icon: <Award size={18} className="text-[#1A73E8]" />,
    title: 'AI Quality Assessment',
    desc: 'Pre-submission manuscript screening for structural completeness, citation quality, and methodological consistency.',
  },
];

const LandingPage = () => {
  const { user } = useAuth();
  const { journals, boardMembers } = useData();
  const navigate = useNavigate();

  const handleSubmitClick = () => {
    if (user) navigate('/submit');
    else navigate('/login');
  };

  // ✅ Real board composition counts from actual data
  const editorsInChief = boardMembers.filter(m => m.role === 'superadmin' || m.category === 'founder');
  const seniorEditors = boardMembers.filter(m => m.role === 'admin' || m.category === 'editor');
  const reviewers = boardMembers.filter(m => m.role === 'reviewer' || m.category === 'reviewer');

  return (
    <div className="bg-white" data-testid="landing-page">

      {/* ── Hero Section ── */}
      <section className="bg-white border-b border-[#E5E7EB]" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <p className="section-overline mb-5">MAH Quantum Research Institute</p>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#1F1F1F] leading-tight mb-6">
                A Trusted Platform for Scholarly and{' '}
                <span className="text-[#1A73E8]">Professional</span>{' '}
                Research Publication
              </h1>
              <p className="text-[#4B5563] text-base leading-relaxed mb-4 max-w-2xl">
                MAH Quantum Research Institute provides a rigorous, transparent, and globally recognized publishing platform — open to academic researchers, professors, PhD scholars, and independent professionals across all disciplines.
              </p>
              <p className="text-[#6B7280] text-sm leading-relaxed mb-8 max-w-2xl border-l-2 border-[#1A73E8] pl-4">
                All submissions follow strict peer-review standards and COPE-compliant publication ethics.
              </p>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={handleSubmitClick}
                  className="flex items-center gap-2 px-7 py-3 bg-[#1A73E8] text-white text-sm font-medium hover:bg-[#174EA6] transition-colors"
                  data-testid="hero-submit-btn"
                >
                  Submit Research Paper <ArrowRight size={15} />
                </button>
                <Link
                  to="/journals"
                  className="flex items-center gap-2 px-7 py-3 border border-[#1A73E8] text-[#1A73E8] text-sm font-medium hover:bg-[#F5F7FA] transition-colors"
                  data-testid="hero-explore-btn"
                >
                  Browse Journals
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5 hidden lg:block">
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1698434156099-5fa64f7fddad?crop=entropy&cs=srgb&fm=jpg&q=85"
                  alt="Researcher reviewing academic papers"
                  className="w-full h-80 object-cover border border-[#E5E7EB]"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-white/95 border-t border-[#E5E7EB] px-5 py-3">
                  <p className="text-xs font-semibold text-[#374151] mb-1.5">Publication Workflow</p>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {['Submission', 'Editorial Check', 'Peer Review', 'Decision', 'Published'].map((step, i) => (
                      <React.Fragment key={step}>
                        <span className="text-xs text-[#6B7280] whitespace-nowrap">{step}</span>
                        {i < 4 && <ChevronRight size={9} className="text-[#D1D5DB] flex-shrink-0" />}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
                <div className="absolute -top-4 -right-4 bg-white border border-[#E5E7EB] px-4 py-3 text-center shadow-sm">
                  <p className="text-[#1A73E8] font-serif text-xl font-bold">6–8 wks</p>
                  <p className="text-xs text-[#6B7280] mt-0.5">Avg. review time</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Trust & Credibility Section ── */}
      <section className="bg-[#F5F7FA] border-b border-[#E5E7EB]" data-testid="trust-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-4">
              <p className="section-overline mb-3">Our Commitment</p>
              <h2 className="font-serif text-3xl lg:text-4xl font-semibold text-[#1F1F1F] leading-tight mb-4">
                Built for Researchers.<br />Defined by Standards.
              </h2>
              <p className="text-[#6B7280] text-sm leading-relaxed">
                Whether you are affiliated with a leading university or conducting independent research, our platform upholds the same rigorous standards of academic integrity and editorial transparency for every submission.
              </p>
            </div>
            <div className="lg:col-span-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-0 border border-[#E5E7EB]">
                {TRUST_POINTS.map((point, i) => (
                  <div
                    key={point.title}
                    className={`bg-white p-5 flex items-start gap-4 ${i < 4 ? 'border-b border-[#E5E7EB]' : ''} ${i % 2 === 0 && i < 4 ? 'sm:border-r sm:border-[#E5E7EB]' : ''} ${i === 4 ? 'sm:col-span-2 sm:border-r-0' : ''}`}
                    data-testid={`trust-point-${i}`}
                  >
                    <div className="w-8 h-8 bg-[#EFF6FF] border border-[#BFDBFE] flex items-center justify-center flex-shrink-0 mt-0.5">
                      {point.icon}
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-[#1F1F1F] mb-1">{point.title}</h4>
                      <p className="text-xs text-[#6B7280] leading-relaxed">{point.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── About Section ── */}
      <section className="bg-white border-b border-[#E5E7EB]" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div>
              <p className="section-overline mb-4">About the Institute</p>
              <h2 className="font-serif text-3xl lg:text-4xl font-semibold text-[#1F1F1F] mb-5 leading-tight">
                A Global Platform for Rigorous and Inclusive Research Publishing
              </h2>
              <p className="text-[#4B5563] leading-relaxed mb-4">
                Founded on the principles of academic integrity and open scholarship, MAH Quantum Research Institute serves as a trusted publication hub for researchers across academic institutions and independent professional settings worldwide.
              </p>
              <p className="text-[#4B5563] leading-relaxed mb-6">
                Our editorial process is guided by COPE principles and supported by an international advisory board of acclaimed scholars. We are committed to fair, transparent, and efficient publication practices — regardless of an author's institutional affiliation.
              </p>
              <ul className="space-y-2.5">
                {[
                  'Double-blind peer review for all submissions',
                  'Designed to meet international indexing and discoverability standards',
                  'Accepted publications assigned DOIs and archived via Zenodo',
                  'COPE-compliant publication ethics policy',
                  'Open to academic and independent researchers',
                ].map(item => (
                  <li key={item} className="flex items-center gap-2.5 text-sm text-[#374151]">
                    <CheckCircle size={14} className="text-[#1A73E8] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1547253113-7ed8c1f3b9ed?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Academic research library"
                className="w-full h-80 object-cover border border-[#E5E7EB]"
              />
              <div className="absolute -bottom-4 -right-4 bg-white border border-[#E5E7EB] p-4 hidden lg:block">
                <p className="text-xs font-semibold text-[#1F1F1F]">Average Review Time</p>
                <p className="text-[#1A73E8] font-serif text-2xl font-bold">6–8 Weeks</p>
                <p className="text-xs text-[#6B7280]">Faster than industry average</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── How It Works Section ── */}
      <section className="bg-[#F5F7FA] border-b border-[#E5E7EB]" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-xl mb-12">
            <p className="section-overline mb-3">Publication Process</p>
            <h2 className="font-serif text-3xl lg:text-4xl font-semibold text-[#1F1F1F]">How It Works</h2>
            <p className="text-[#6B7280] text-sm mt-3 leading-relaxed">
              A structured, transparent three-stage process from manuscript submission to global publication.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-[#E5E7EB]">
            {HOW_IT_WORKS.map((item, i) => (
              <div
                key={item.title}
                className={`bg-white p-8 relative ${i < 2 ? 'border-b md:border-b-0 md:border-r border-[#E5E7EB]' : ''}`}
                data-testid={`how-step-${i}`}
              >
                <div className="flex items-start gap-4 mb-5">
                  <span className="font-serif text-5xl font-bold text-[#E5E7EB] leading-none select-none">{item.step}</span>
                  <div className="w-11 h-11 bg-[#EFF6FF] border border-[#BFDBFE] flex items-center justify-center flex-shrink-0 mt-1">
                    {item.icon}
                  </div>
                </div>
                <h3 className="font-serif text-xl font-semibold text-[#1F1F1F] mb-3">{item.title}</h3>
                <p className="text-[#6B7280] text-sm leading-relaxed">{item.desc}</p>
                {i < 2 && (
                  <div className="hidden md:block absolute -right-4 top-1/2 -translate-y-1/2 z-10">
                    <div className="w-8 h-8 bg-white border border-[#E5E7EB] flex items-center justify-center">
                      <ArrowRight size={14} className="text-[#1A73E8]" />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Standards / Features Section ── */}
      <section className="bg-white border-b border-[#E5E7EB]" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-xl mb-12">
            <p className="section-overline mb-3">Publication Standards</p>
            <h2 className="font-serif text-3xl lg:text-4xl font-semibold text-[#1F1F1F]">Why Researchers Trust MAH</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-[#E5E7EB]">
            {FEATURES.map((feature, i) => (
              <div
                key={feature.title}
                className={`p-8 bg-white ${i < 2 ? 'border-b md:border-b-0 md:border-r border-[#E5E7EB]' : ''}`}
                data-testid={`feature-card-${i}`}
              >
                <div className="w-10 h-10 bg-[#F5F7FA] border border-[#E5E7EB] flex items-center justify-center mb-5">
                  {feature.icon}
                </div>
                <h3 className="font-serif text-xl font-semibold text-[#1F1F1F] mb-3">{feature.title}</h3>
                <p className="text-[#6B7280] text-sm leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Journals Preview ── */}
      <section className="bg-[#F5F7FA] border-b border-[#E5E7EB]" data-testid="journals-preview-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="section-overline mb-3">Our Publications</p>
              <h2 className="font-serif text-3xl lg:text-4xl font-semibold text-[#1F1F1F]">Featured Journals</h2>
              <p className="text-[#6B7280] text-sm mt-2 max-w-xl">
                Peer-reviewed, internationally indexed journals covering key areas of scientific and technological research.
              </p>
            </div>
            <Link
              to="/journals"
              className="hidden sm:flex items-center gap-1.5 text-sm text-[#1A73E8] hover:text-[#174EA6] font-medium transition-colors flex-shrink-0"
              data-testid="view-all-journals-link"
            >
              View all journals <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {journals.slice(0, 3).map(journal => (
              <JournalCard key={journal.id} journal={journal} />
            ))}
          </div>
          <div className="mt-5 sm:hidden text-center">
            <Link to="/journals" className="text-sm text-[#1A73E8] hover:underline flex items-center justify-center gap-1">
              View all journals <ArrowRight size={13} />
            </Link>
          </div>
        </div>
      </section>

      {/* ── Academic & Research Advisory Board ── */}
      <section className="bg-white border-b border-[#E5E7EB]" data-testid="academic-board-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-10">
            <div className="lg:col-span-7">
              <p className="section-overline mb-3">Leadership & Governance</p>
              <h2 className="font-serif text-3xl lg:text-4xl font-semibold text-[#1F1F1F]">
                Academic &amp; Research Advisory Board
              </h2>
              <p className="text-[#6B7280] mt-3 text-sm leading-relaxed max-w-xl">
                Our board comprises internationally recognized professors, senior researchers, and domain experts from leading institutions worldwide. They uphold scientific integrity and editorial excellence across all MAH publications.
              </p>
            </div>

            {/* ✅ Real board composition from actual data */}
            <div className="lg:col-span-5 hidden lg:flex items-end">
              <div className="w-full border border-[#E5E7EB] bg-[#F5F7FA] px-5 py-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-[#6B7280] mb-3">Board Composition</p>
                <div className="grid grid-cols-3 gap-2 text-center">
                  {[
                    { label: 'Editors-in-Chief', count: editorsInChief.length || 0 },
                    { label: 'Senior Editors', count: seniorEditors.length || 0 },
                    { label: 'Reviewers', count: reviewers.length || 0 },
                  ].map(c => (
                    <div key={c.label}>
                      <p className="font-serif text-2xl font-bold text-[#1A73E8]">{c.count}</p>
                      <p className="text-xs text-[#6B7280]">{c.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {boardMembers.slice(0, 8).map(member => (
              <AcademicBoardCard key={member.id} member={member} />
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link
              to="/editorial-board"
              className="inline-flex items-center gap-2 px-6 py-2.5 border border-[#E5E7EB] text-sm text-[#4B5563] hover:bg-[#F5F7FA] hover:border-[#1A73E8] hover:text-[#1A73E8] transition-colors"
              data-testid="view-full-board-link"
            >
              View Full Editorial &amp; Advisory Board <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* ── AI-Assisted Publishing (Coming Soon) ── */}
      <section className="bg-[#F5F7FA] border-b border-[#E5E7EB]" data-testid="ai-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 mb-4">
              <Zap size={12} className="text-[#1A73E8]" />
              <span className="text-xs font-semibold text-[#1A73E8] uppercase tracking-wider">Platform Roadmap</span>
            </div>
            <h2 className="font-serif text-3xl lg:text-4xl font-semibold text-[#1F1F1F] mb-3">AI-Assisted Publishing</h2>
            <p className="text-[#6B7280] text-sm leading-relaxed">
              We are developing intelligent tools to streamline the research submission and review process. These features will be available in a future platform update.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-[#E5E7EB]">
            {AI_TOOLS.map((tool, i) => (
              <div
                key={tool.title}
                className={`bg-white p-8 ${i < 2 ? 'border-b md:border-b-0 md:border-r border-[#E5E7EB]' : ''}`}
                data-testid={`ai-tool-${i}`}
              >
                <div className="flex items-start justify-between mb-5">
                  <div className="w-10 h-10 bg-[#F5F7FA] border border-[#E5E7EB] flex items-center justify-center opacity-70">
                    {tool.icon}
                  </div>
                  <span className="text-xs px-2 py-0.5 bg-blue-50 text-[#1A73E8] border border-blue-200 font-semibold tracking-wide whitespace-nowrap">
                    Coming Soon
                  </span>
                </div>
                <h3 className="font-serif text-lg font-semibold text-[#374151] mb-2">{tool.title}</h3>
                <p className="text-[#9CA3AF] text-sm leading-relaxed">{tool.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA Section ── */}
      <section className="bg-[#174EA6]" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <p className="text-blue-300 text-xs font-semibold uppercase tracking-widest mb-4">Begin Your Submission</p>
              <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-white mb-4 leading-tight">
                Advance Your Research Through Rigorous and Inclusive Publishing
              </h2>
              <p className="text-blue-200 text-sm leading-relaxed mb-6 max-w-lg">
                Submit your manuscript to a globally indexed, peer-reviewed journal. Our structured workflow ensures your work is evaluated with fairness, expertise, and academic integrity — from submission to publication.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleSubmitClick}
                  className="px-7 py-3 bg-white text-[#174EA6] text-sm font-semibold hover:bg-blue-50 transition-colors"
                  data-testid="cta-submit-btn"
                >
                  Submit Research Paper
                </button>
                <Link
                  to="/register"
                  className="px-7 py-3 border border-white/40 text-white text-sm font-medium hover:bg-white/10 transition-colors text-center"
                  data-testid="cta-register-btn"
                >
                  Create an Account
                </Link>
              </div>
            </div>
            <div className="hidden lg:block">
              <div className="border border-white/20 bg-white/5 p-6">
                <p className="text-xs font-semibold uppercase tracking-wider text-blue-200 mb-4">Submission Standards</p>
                <ul className="space-y-3">
                  {[
                    'COPE-compliant publication ethics',
                    'Double-blind peer review process',
                    'Structured editorial decision timeline',
                    'DOI registration for all accepted papers',
                    'Open to academic and independent authors',
                  ].map(item => (
                    <li key={item} className="flex items-center gap-2.5 text-sm text-blue-100">
                      <CheckCircle size={13} className="text-blue-300 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default LandingPage;
